import React, { useEffect, useState } from "react";
import DateRangePicker from "../elements/DateRangePicker";
import Pagination from "../elements/Pagination";
import searchIcon from "../../resources/icons/search.svg";
import downArrowIcon from "../../resources/icons/down_pointer.svg";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import axios from "axios";

const AdminStats: React.FC = () => {
  const navigate = useNavigate();
  const rowsPerPage = 10;

  const [projects, setProjects] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [tempSearch, setTempSearch] = useState("");
  const [search, setSearch] = useState("");
  const [tempStartDate, setTempStartDate] = useState("");
  const [tempEndDate, setTempEndDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortOrder, setSortOrder] = useState<string>("");
  const [dateRangeResetKey, setDateRangeResetKey] = useState(0);

  const applyFilters = () => {
    setSearch(tempSearch);
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setTempSearch("");
    setTempStartDate("");
    setTempEndDate("");
    setSearch("");
    setStartDate("");
    setEndDate("");
    setSortOrder("");
    setCurrentPage(1);
    setDateRangeResetKey((prev) => prev + 1);
  };

  const handleSortByName = () => {
    if (sortOrder === "") setSortOrder("asc");
    else if (sortOrder === "asc") setSortOrder("desc");
    else setSortOrder("");
  };

  useEffect(() => {
    const fetchProjects = async () => {
      const token = localStorage.getItem("token");
      const queryParams = new URLSearchParams();
      queryParams.append("page", currentPage.toString());
      queryParams.append("limit", rowsPerPage.toString());
      if (search) queryParams.append("name", search);
      if (startDate && endDate) {
        queryParams.append("completedStartDate", startDate);
        queryParams.append("completedEndDate", endDate);
      }
      if (sortOrder) queryParams.append("sort", sortOrder);

      try {
        const res = await axios.get(
          `${process.env.REACT_APP_URL}/admin/completed-projects?${queryParams}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "X-Client-Type": "web",
            },
          }
        );
        setProjects(res.data.projects || []);
        setTotalPages(res.data.totalPages || 1);
      } catch {}
    };

    fetchProjects();
  }, [currentPage, search, startDate, endDate, sortOrder]);

  return (
    <div className="flex flex-row">
      <div className="flex-1 p-6 ml-[17.5rem]">
        <h1 className="text-2xl font-bold pb-4 mb-4 border-b border-[#EAECF0]">
          Completed projects
        </h1>

        <div className="bg-white mb-6">
          <div className="flex justify-between gap-4 border-[#D0D5DD] pb-4">
            <div className="flex flex-col w-[20rem]">
              <label className="text-sm font-medium text-[#344054] mb-1">
                Project
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  <img src={searchIcon} alt="Search" className="w-5 h-5" />
                </span>
                <input
                  type="text"
                  placeholder="Search by project name..."
                  value={tempSearch}
                  onChange={(e) => setTempSearch(e.target.value)}
                  className="pl-10 pr-3 py-2 border border-[#D0D5DD] rounded w-full text-sm shadow-sm"
                />
              </div>
            </div>

            <div className="flex-1 flex flex-col">
              <label className="text-sm font-medium text-[#344054] mb-1">
                Completion date
              </label>
              <DateRangePicker
                key={dateRangeResetKey}
                onChange={(start, end) => {
                  setTempStartDate(start);
                  setTempEndDate(end);
                }}
              />
            </div>

            <div className="flex items-end gap-6">
              <button
                onClick={clearFilters}
                className="px-2 py-2 text-[#344054] text-nowrap"
              >
                Clear Filters
              </button>
              <button
                onClick={applyFilters}
                className="px-2 py-2 bg-buttonBlue text-white rounded text-nowrap"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>

        <div className="relative overflow-x-auto border rounded-xl border-[#EAECF0] mb-4">
          <table className="min-w-full table-fixed">
            <thead className="bg-[#F9FAFB] border-b border-[#EAECF0] text-left text-xs font-medium text-[#475467] text-nowrap">
              <tr>
                <th className="px-6 py-3 flex items-center gap-1">
                  Project name
                  <button onClick={handleSortByName}>
                    <img
                      src={downArrowIcon}
                      alt="Sort"
                      className={`w-4 h-4 transform transition-transform duration-200 ${
                        sortOrder === "asc" ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                </th>
                <th className="px-6 py-3">Publication date</th>
                <th className="px-6 py-3">Completion date</th>
                <th className="px-6 py-3">Assignor</th>
                <th className="px-6 py-3 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EAECF0]">
              {projects.map((project, i) => (
                <tr
                  key={project.id}
                  className={i % 2 === 0 ? "bg-[#F9FAFB]" : "bg-white"}
                >
                  <td
                    className="px-6 py-4 truncate max-w-[12rem]"
                    title={project.title}
                  >
                    {project.title}
                  </td>
                  <td className="px-6 py-4">
                    {dayjs(project.publishedAt).format("MMM D, YYYY")}
                  </td>
                  <td className="px-6 py-4">
                    {dayjs(project.closedAt).format("MMM D, YYYY")}
                  </td>
                  <td className="px-6 py-4">{project.assignor?.name || "—"}</td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() =>
                        navigate(`/admin/statistics/${project.id}`)
                      }
                      className="text-buttonBlue font-semibold whitespace-nowrap"
                    >
                      See statistics
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="border-t mt-4 mb-4">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminStats;
